import React, { useId } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import ArrowUpRightIcon from './CustomIcons/ArrowUpRightIcon';
import InfoFilledIcon from './CustomIcons/InfoFilledIcon';
import FormItem from './FormItem';
import Link from './Link';
import { safeHref } from './safeHref';
import Spacer from './Spacer';
import Tooltip from './Tooltip';
import MarkdownBlock from './Typography/MarkdownBlock';
import Text from './Typography/Text';
import { FOCUS_RING } from './focusRing';
import { type FormItemChildren, NO_FIELD_ERROR, renderFormItemChildren } from './formItemState';

/** Tooltip's own default of 1s reads as broken; 0.5 matches the rest of the library. */
const TOOLTIP_DELAY_SECONDS = 0.5;

/** The ids that wire a form field's label / description / hint to its control. */
export interface FormFieldIds {
    controlId: string;
    labelId?: string;
    descriptionId?: string;
    hintId?: string;
    helperTextId?: string;
    describedBy?: string;
}

interface FormFieldIdOptions {
    /** A caller-supplied id always wins, so consumers can label the control themselves. */
    id?: string;
    label?: string;
    description?: string;
    hint?: string;
    helperText?: boolean;
}

/**
 * Generates the stable ids a form field needs to be announced correctly. Shared by
 * `Input`, `TextArea`, `Dropdown` and `MultiSelect` so all four wire up identically.
 */
export const useFormFieldIds = ({
    id,
    label,
    description,
    hint,
    helperText = false,
}: FormFieldIdOptions): FormFieldIds => {
    // useId() emits ":r0:", and a colon is invalid in a CSS id selector, so a consumer's
    // querySelector(`#${id}`) throws. Stripping only the delimiters keeps ids unique.
    const generatedId = `mal-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}-`;
    const controlId = id ?? `${generatedId}control`;
    const labelId = label ? `${generatedId}label` : undefined;
    const descriptionId = description ? `${generatedId}description` : undefined;
    const hintId = hint ? `${generatedId}hint` : undefined;
    const helperTextId = helperText ? `${generatedId}helper` : undefined;
    const describedBy =
        [descriptionId, hintId, helperTextId].filter(Boolean).join(' ') || undefined;

    return { controlId, labelId, descriptionId, hintId, helperTextId, describedBy };
};

interface BaseFormComponentProps {
    label?: string;
    description?: string;
    hint?: string;
    rightSideContent?: RightSideContent;
    tooltip?: string;
    required?: boolean;
    requiredIndicator?: RequiredIndicator;
    name: FieldPath<FieldValues>;
    defaultValue?: string;
    loading?: boolean;
    formControl?: Control<FieldValues, unknown>;
    loadingComponent: React.ReactNode;
    children?: FormItemChildren;
    /** Accessibility wiring, from `useFormFieldIds` — see `FormFieldIds`. */
    controlId?: string;
    labelId?: string;
    descriptionId?: string;
    hintId?: string;
    fullWidth?: boolean;
}

const BaseFormComponent: React.FC<BaseFormComponentProps> = ({
    label,
    hint,
    rightSideContent,
    tooltip,
    name,
    description,
    required = false,
    requiredIndicator = 'optional-suffix',
    loading = false,
    formControl,
    loadingComponent,
    children,
    controlId,
    labelId,
    descriptionId,
    hintId,
    fullWidth = false,
}) => {
    const finalComponent = formControl ? (
        <FormItem control={formControl} name={name}>
            {children}
        </FormItem>
    ) : (
        renderFormItemChildren(children, NO_FIELD_ERROR)
    );

    const topHeader = () => (
        <Spacer direction="vertical" size={4} align="stretch">
            <div className="flex justify-between items-start">
                <Label
                    text={label}
                    required={required}
                    requiredIndicator={requiredIndicator}
                    tooltip={tooltip}
                    id={labelId}
                    // No control is rendered while loading, so an htmlFor would be an orphan.
                    htmlFor={loading ? undefined : controlId}
                />
                {(hint || rightSideContent) && (
                    <div className="flex items-start gap-2">
                        {hint && <Hint text={hint} id={hintId} />}
                        {rightSideContent && <RightSide {...rightSideContent} />}
                    </div>
                )}
            </div>
            {description && <Description text={description} id={descriptionId} />}
        </Spacer>
    );

    const showHeader = label || hint || description || rightSideContent;

    return (
        <Spacer direction="vertical" size={6} align="stretch" fullWidth={fullWidth}>
            {showHeader && topHeader()}
            {!loading ? finalComponent : loadingComponent}
        </Spacer>
    );
};

/**
 * What sits at the far end of a field's label row. Data rather than a node, so nothing
 * arbitrary reaches the row; discriminated so a further kind is additive. Plain text is
 * already `hint`.
 */
export type RightSideContent = {
    type?: 'link';
    text: string;
    href: string;
};

const RightSide = (content: RightSideContent) => {
    const safe = safeHref(content.href);

    if (!safe) {
        return null;
    }

    return (
        <Link
            href={safe}
            target="_blank"
            rel="noopener noreferrer"
            color="dimmed"
            size="xsmall"
            underline={false}
            className="shrink-0 whitespace-nowrap inline-flex items-center gap-1 relative top-[3px]"
        >
            {content.text}
            <ArrowUpRightIcon size={13} />
        </Link>
    );
};

const Hint = ({ text, id }: { text?: string; id?: string }) => {
    return (
        <span id={id}>
            <Text size="xsmall" color="dimmed" className="pr-2 relative top-[3px]">
                {text}
            </Text>
        </span>
    );
};

const Description = ({ text, id }: { text?: string; id?: string }) => {
    return (
        <div className="pl-2 text-left" id={id}>
            <MarkdownBlock color="dimmed" size="small" text={text ?? ''} omitMargin={true} />
        </div>
    );
};

export type RequiredIndicator = 'optional-suffix' | 'asterisk' | 'none';

const Label = ({
    text,
    required,
    requiredIndicator,
    tooltip,
    id,
    htmlFor,
}: {
    text?: string;
    required: boolean;
    requiredIndicator: RequiredIndicator;
    tooltip?: string;
    id?: string;
    htmlFor?: string;
}) => {
    return text ? (
        <div className="flex items-center">
            <label id={id} htmlFor={htmlFor}>
                <Text size="xsmall" color="primary" fontWeight="semi-bold" className="pl-2">
                    {text}
                </Text>
            </label>
            {/* Sibling of the <label>, not a child: Testing Library's getByLabelText matches
                on textContent, so a marker inside it breaks getByLabelText('<label>'). */}
            {requiredIndicator === 'optional-suffix' && !required && (
                <span aria-hidden="true">
                    <Text size="xsmall" color="dimmed" className="ml-1">
                        (Optional)
                    </Text>
                </span>
            )}
            {requiredIndicator === 'asterisk' && required && (
                <span
                    aria-hidden="true"
                    className="ml-1 text-[var(--malachite-destructive-foreground,rgb(196,62,62))]"
                >
                    *
                </span>
            )}
            {tooltip && (
                <Tooltip text={tooltip} placement="right" delay={TOOLTIP_DELAY_SECONDS}>
                    <button
                        type="button"
                        aria-label={`More information about ${text}`}
                        className={`ml-1 -mt-[2px] inline-flex items-center text-[var(--malachite-text-dimmed)] cursor-help ${FOCUS_RING}`}
                    >
                        <InfoFilledIcon size={14} />
                    </button>
                </Tooltip>
            )}
        </div>
    ) : (
        <div />
    );
};

export default BaseFormComponent;
